import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  createContainer,
  deleteContainer,
  patchContainer,
} from "@/features/transit/api/transit.api"
import type {
  CreateContainerPayload,
  PatchContainerPayload,
} from "@/features/transit/api/types"
import { transitKeys } from "@/features/transit/hooks/use-transit-orders"
import { resolveApiError } from "@/lib/api/error-i18n"

// Force a synchronous refetch of the active OT detail. We don't trust
// the mutation response payload for state — backend may return a partial
// embed shape that strips lots/docs/blockers. Refetching guarantees the
// UI sees the canonical post-mutation state.
function useInvalidate(otId: number | string) {
  const queryClient = useQueryClient()
  return async () => {
    await queryClient.refetchQueries({
      queryKey: transitKeys.detail(otId),
      type: "active",
    })
    await queryClient.invalidateQueries({ queryKey: transitKeys.all })
  }
}

function useToastError() {
  const { t } = useTranslation("transit")
  return (error: unknown) => toast.error(resolveApiError(error, t))
}

export function useCreateContainer(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (payload: CreateContainerPayload) =>
      createContainer(otId, payload),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function usePatchContainer(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: ({
      containerId,
      payload,
    }: {
      containerId: number
      payload: PatchContainerPayload
    }) => patchContainer(otId, containerId, payload),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useDeleteContainer(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (containerId: number) => deleteContainer(otId, containerId),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

